import * as React from 'react';
import { createRef } from 'react';

import classnames from 'classnames';

import InternationalPhoneNumberInput from 'bundles/account-profile/components/i18n-phone-number-input/InternationalPhoneNumberInput';
import type { Phone } from 'bundles/naptimejs/resources/__generated__/ProfilesV1';
import CDSDropdownElement from 'bundles/survey-form/components/CDSDropdownElement';
import CDSTextInputElement from 'bundles/survey-form/components/CDSTextInputElement';
import CheckboxDropdown from 'bundles/survey-form/components/CheckboxDropdown';
import { QuestionTypes } from 'bundles/survey-form/constants/SurveyFormConstants';
import type { QuestionType } from 'bundles/survey-form/constants/SurveyFormConstants';
import { getQuestionType } from 'bundles/survey-form/utils/SurveyFormUtils';
import {
  customFormattedPhoneStyles,
  formChangeFormatter,
} from 'bundles/university-program-qualification/components/SurveyQuestion';

import _t from 'i18n!nls/xddp';

const { SHORT_ANSWER, DROPDOWN, NUMERIC, CHECKBOX } = QuestionTypes;

type Props = {
  questionInfo: {
    id: string;
    content: {
      questionType: string;
    };
    defaultValue?: string;
    isRequired?: boolean;
  };
  validationError: string;
  handleInputChangeFunc: (
    questionId: string,
    questionType: QuestionType
  ) => (response: { text?: string; ids?: Array<string> }) => void;
  setRegion: (region: string) => void;
};

class EoiQuestion extends React.Component<Props> {
  renderQuestionLabel = (prompt: string, validationError: string) => {
    const labelClassName = classnames('question-label', {
      'question-error': !!validationError,
    });
    return (
      <div className={labelClassName}>
        <div className="question-prompt label-text m-y-1s">{prompt}</div>
        {validationError && <div className="question-error-message caption-text">{validationError}</div>}
      </div>
    );
  };

  renderQuestionByType = () => {
    const { questionInfo, validationError, handleInputChangeFunc, setRegion } = this.props;

    const questionType = getQuestionType(questionInfo);
    const questionId = questionInfo.id;
    const defaultValue = questionInfo.defaultValue;

    const enableErrorState = validationError !== undefined;
    const numericType = questionType === NUMERIC;

    const inputRef = createRef<HTMLInputElement>();

    switch (questionType) {
      case NUMERIC:
      case SHORT_ANSWER: {
        // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        const { prompt, placeholder } = questionInfo.content[questionType];
        let renderedElement;

        if (prompt === 'Phone Number') {
          renderedElement = (
            <InternationalPhoneNumberInput
              profile={{ phone: defaultValue as unknown as Phone }}
              customStyles={customFormattedPhoneStyles}
              isEmptyValid={false}
              submissionError={!!validationError}
              isOptional={!questionInfo?.isRequired}
              formChangeFormatter={formChangeFormatter}
              handleFormChange={handleInputChangeFunc(questionId, questionType)}
              setRegion={setRegion}
              ref={inputRef}
            />
          );
        } else {
          renderedElement = (
            <CDSTextInputElement
              validationError={validationError}
              prompt={prompt}
              onChange={handleInputChangeFunc(questionId, questionType)}
              placeholder={placeholder ?? _t('Enter #{fieldName}', { fieldName: prompt })}
              defaultValue={defaultValue ?? ''}
              numericType={numericType}
              id={questionId}
              isRequired={questionInfo?.isRequired}
            />
          );
        }
        return <div className="m-y-1 rc-EoiQuestion">{renderedElement}</div>;
      }
      case DROPDOWN: {
        // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        const { options, placeholder, prompt } = questionInfo.content[questionType];
        return (
          <div className="m-y-1 rc-EoiQuestion">
            <CDSDropdownElement
              validationError={validationError}
              prompt={prompt}
              placeholder={placeholder ?? _t('Select #{fieldName}', { fieldName: prompt })}
              onChange={handleInputChangeFunc(questionId, questionType)}
              options={options}
              isRequired={questionInfo?.isRequired}
              defaultValue={defaultValue}
            />
          </div>
        );
      }
      case CHECKBOX: {
        // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        const { prompt, options } = questionInfo.content[questionType];
        return (
          <div className="m-y-1 rc-EoiQuestion">
            {this.renderQuestionLabel(prompt, validationError)}
            <CheckboxDropdown
              options={options.map((optionData: $TSFixMe) => ({
                label: optionData.text,
                value: optionData.id,
              }))}
              enableErrorState={enableErrorState}
              onChange={handleInputChangeFunc(questionId, questionType)}
            />
          </div>
        );
      }

      default: {
        return null;
      }
    }
  };

  render() {
    return this.renderQuestionByType();
  }
}

export default EoiQuestion;
